import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { tap } from 'rxjs';
import { UiSelectOptions } from '../../models/ui-select-option.model';
import { IdName } from '../../models/id-name';

const MINUTES_IN_DAY = 24 * 60; // 1440 minutes

@Component({
  selector: 'app-ui-timepicker',
  templateUrl: './ui-timepicker.component.html',
  styleUrl: './ui-timepicker.component.scss',
  standalone: false,
})
export class UiTimepickerComponent implements OnInit, OnChanges {
  @Input() label = '';
  @Input() control: FormControl<string | undefined>;
  @Input() description = '';
  @Input() required = true;
  @Input() initialMinTimeLimiter?: string[];
  @Input() initialMaxTimeLimiter?: string[];
  @Input() matTimepickerMin?: string;
  @Input() matTimepickerMax?: string;

  @Output() readonly changeHandler: EventEmitter<string> = new EventEmitter<string>();

  private allTimeOptions: IdName[] = [
    { id: '00:00:00', name: '00:00' },
    { id: '00:30:00', name: '00:30' },
    { id: '01:00:00', name: '01:00' },
    { id: '01:30:00', name: '01:30' },
    { id: '02:00:00', name: '02:00' },
    { id: '02:30:00', name: '02:30' },
    { id: '03:00:00', name: '03:00' },
    { id: '03:30:00', name: '03:30' },
    { id: '04:00:00', name: '04:00' },
    { id: '04:30:00', name: '04:30' },
    { id: '05:00:00', name: '05:00' },
    { id: '05:30:00', name: '05:30' },
    { id: '06:00:00', name: '06:00' },
    { id: '06:30:00', name: '06:30' },
    { id: '07:00:00', name: '07:00' },
    { id: '07:30:00', name: '07:30' },
    { id: '08:00:00', name: '08:00' },
    { id: '08:30:00', name: '08:30' },
    { id: '09:00:00', name: '09:00' },
    { id: '09:30:00', name: '09:30' },
    { id: '10:00:00', name: '10:00' },
    { id: '10:30:00', name: '10:30' },
    { id: '11:00:00', name: '11:00' },
    { id: '11:30:00', name: '11:30' },
    { id: '12:00:00', name: '12:00' },
    { id: '12:30:00', name: '12:30' },
    { id: '13:00:00', name: '13:00' },
    { id: '13:30:00', name: '13:30' },
    { id: '14:00:00', name: '14:00' },
    { id: '14:30:00', name: '14:30' },
    { id: '15:00:00', name: '15:00' },
    { id: '15:30:00', name: '15:30' },
    { id: '16:00:00', name: '16:00' },
    { id: '16:30:00', name: '16:30' },
    { id: '17:00:00', name: '17:00' },
    { id: '17:30:00', name: '17:30' },
    { id: '18:00:00', name: '18:00' },
    { id: '18:30:00', name: '18:30' },
    { id: '19:00:00', name: '19:00' },
    { id: '19:30:00', name: '19:30' },
    { id: '20:00:00', name: '20:00' },
    { id: '20:30:00', name: '20:30' },
    { id: '21:00:00', name: '21:00' },
    { id: '21:30:00', name: '21:30' },
    { id: '22:00:00', name: '22:00' },
    { id: '22:30:00', name: '22:30' },
    { id: '23:00:00', name: '23:00' },
    { id: '23:30:00', name: '23:30' },
    { id: '23:59:59', name: '24:00' },
  ];

  timeOptions: UiSelectOptions = [];

  ngOnInit(): void {
    // initial limits if any
    if (this.initialMinTimeLimiter) {
      this.putInitialLimits(this.initialMinTimeLimiter);
    }
    if (this.initialMaxTimeLimiter) {
      this.putInitialLimits(this.initialMaxTimeLimiter);
    }

    this.filterTimeOptions();

    this.control.valueChanges
      .pipe(
        tap((value) => {
          if (value) {
            this.changeHandler.emit(value);
          }
        })
      )
      .subscribe();
  }

  ngOnChanges(): void {
    this.filterTimeOptions();
  }

  clearAndEnableControl(): void {
    this.control.reset();
    this.control.enable();
  }

  private filterTimeOptions(): void {
    if (!this.allTimeOptions) {
      this.timeOptions = [];
      return;
    }

    // Check if min/max constraints are actually provided (not empty strings or undefined)
    const hasMinConstraint = this.matTimepickerMin && this.matTimepickerMin.trim() !== '';
    const hasMaxConstraint = this.matTimepickerMax && this.matTimepickerMax.trim() !== '';

    // If no min/max constraints are provided, show all options
    if (!hasMinConstraint && !hasMaxConstraint) {
      this.timeOptions = [...this.allTimeOptions];
      return;
    }

    let minTime = this.parseTimeToMinutes(hasMinConstraint ? this.matTimepickerMin! : '00:00:00');
    let maxTime = this.parseTimeToMinutes(hasMaxConstraint ? this.matTimepickerMax! : '23:59:59');

    // this is added because min value or max value below are true
    // but we always need to force user to add time with at least 30 minutes interval
    const requiredInterval = 30;
    if (hasMinConstraint) {
      minTime += requiredInterval;
    }

    if (hasMaxConstraint) {
      maxTime -= requiredInterval;
    }

    // edge case because 24:00 is not a valid time, but 23:59:59 is
    if (minTime === MINUTES_IN_DAY) {
      maxTime = MINUTES_IN_DAY;
    }

    this.timeOptions = this.allTimeOptions.filter((option) => {
      const optionTime = this.parseTimeToMinutes(option.id as string);
      return optionTime >= minTime && optionTime <= maxTime;
    });

    // Check if current control value is still valid within the filtered range
    if (this.control.value) {
      const currentTime = this.parseTimeToMinutes(this.control.value);
      if (currentTime < minTime || currentTime > maxTime) {
        // Clear the control if the current value is outside the valid range
        this.control.setValue(undefined, { emitEvent: false });
      }
    }
  }

  private parseTimeToMinutes(timeString: string): number {
    if (!timeString || typeof timeString !== 'string') {
      return 0;
    }

    // Special case: '23:59:59' represents '24:00'
    if (timeString === '23:59:59') {
      return MINUTES_IN_DAY;
    }

    const parts = timeString.split(':');
    if (parts.length < 2 || parts.length > 3) {
      return 0;
    }

    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return 0;
    }

    return hours * 60 + minutes;
  }

  private putInitialLimits(limits: string[]): void {
    this.allTimeOptions = this.allTimeOptions.filter(
      (option) => !limits.includes(option.name as string)
    );
  }
}
